package com.nonopn.skydome.common.app.pages.main.model

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

import com.nonopn.icons.Pin
import com.nonopn.skydome.common.app.pages.main.LocalMapScreenModel
import com.nonopn.skydome.common.app.pages.main.model.api.AeroguardLocation
import com.nonopn.skydome.common.app.pages.main.model.api.Probe
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.pages.main.model.api.ThunderDetectionActivity
import com.nonopn.skydome.common.app.pages.main.view.map.ShowNgActivityInfo
import com.nonopn.skydome.common.app.pages.main.view.map.ShowProject
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.maps.LatLng
import com.nonopn.skywatcher.maps.XY

import eu.codlab.compose.widgets.TextNormal
import ovh.plrapps.mapcompose.api.addCallout
import ovh.plrapps.mapcompose.api.removeCallout
import ovh.plrapps.mapcompose.ui.state.MapState

sealed class Composer<T, S>(
    protected val mapState: MapState,
    protected val clusterBackgroundColor: Color,
    protected val flushCallouts: () -> Unit,
    protected val clusterWidth: Dp = 50.dp,
    protected val clusterFontSize: TextUnit = 12.sp
) {
    @Composable
    fun cluster(size: Int) {
        // Here we can customize the cluster style
        Box(
            modifier = Modifier
                .background(clusterBackgroundColor, shape = CircleShape)
                .size(clusterWidth),
            contentAlignment = Alignment.Center
        ) {
            TextNormal(
                text = size.toString(),
                color = AppColor.WhiteCream,
                fontSize = clusterFontSize
            )
        }
    }

    abstract fun compose(
        states: S,
        model: T,
        xy: XY,
        onSelected: (T) -> Unit
    ): @Composable () -> Unit
}

class ComposeSelectedPositionHolder(
    mapState: MapState,
    flushCallouts: () -> Unit
) : Composer<LatLng, MainScreenModelState>(
    mapState,
    AppColor.BackgroundDarkBlueSemiTransparent,
    flushCallouts
) {
    override fun compose(
        states: MainScreenModelState,
        model: LatLng,
        xy: XY,
        onSelected: (LatLng) -> Unit
    ): @Composable (() -> Unit) {
        val id = "selected_position"

        return {
            Column(
                // normally 22 x 30
                Modifier.width(14.dp).height(20.dp)
                    .clickable {
                        flushCallouts()
                        mapState.addCallout(
                            id,
                            xy.x,
                            xy.y
                        ) {
                            ShowNgActivityInfo(
                                Modifier.width(260.dp).padding(bottom = 10.dp),
                                model
                            ) {
                                onSelected(model)
                                mapState.removeCallout(id)
                            }
                        }
                    }
            ) {
                Icon(
                    painter = rememberVectorPainter(Icons.Rounded.Pin),
                    contentDescription = "Selected Position",
                    tint = AppColor.Blue,
                )
            }
        }
    }
}

class ComposeProjectHolder(
    mapState: MapState,
    flushCallouts: () -> Unit
) : Composer<Project, MainScreenModelState>(
    mapState,
    AppColor.BackgroundDarkBlueSemiTransparent,
    flushCallouts
) {
    override fun compose(
        states: MainScreenModelState,
        model: Project,
        xy: XY,
        onSelected: (Project) -> Unit
    ): @Composable (() -> Unit) {
        val id = "${model.latitude}${model.longitude}_callout"
        return {
            Column(
                // normally 22 x 30
                Modifier.width(14.dp).height(20.dp)
                    .clickable {
                        flushCallouts()
                        mapState.addCallout(
                            id,
                            xy.x,
                            xy.y
                        ) {
                            ShowProject(
                                Modifier.width(260.dp).padding(bottom = 10.dp),
                                model
                            ) {
                                onSelected(model)
                                mapState.removeCallout(id)
                            }
                        }
                    }
            ) {
                Icon(
                    painter = rememberVectorPainter(Icons.Rounded.Pin),
                    contentDescription = "Project",
                    tint = AppColor.White,
                )
            }
        }
    }
}

class ComposeProbeHolder(
    mapState: MapState,
    flushCallouts: () -> Unit
) : Composer<Probe, MainScreenModelState>(
    mapState,
    AppColor.BackgroundDarkBlueSemiTransparent,
    flushCallouts
) {
    override fun compose(
        states: MainScreenModelState,
        model: Probe,
        xy: XY,
        onSelected: (Probe) -> Unit
    ): @Composable (() -> Unit) {
        val id = "${model.latitude}${model.longitude}_callout"
        return {
            val mapModel = LocalMapScreenModel.current
            val mapStates by mapModel.states.collectAsState()

            val selected = mapStates.selectedProbes.any { it.id() == model.id() }
            val modifier = if (selected) {
                Modifier.width(21.dp).height(30.dp)
            } else {
                Modifier.width(14.dp).height(20.dp)
            }
            val color = if (selected) {
                AppColor.RedDarker
            } else {
                AppColor.Red
            }

            Column(
                // normally 22 x 30
                modifier.clickable {
                    flushCallouts()
                    mapState.addCallout(
                        id,
                        xy.x,
                        xy.y
                    ) {
                        onSelected(model)
                    }
                }
            ) {
                Icon(
                    painter = rememberVectorPainter(Icons.Rounded.Pin),
                    contentDescription = "Probe",
                    tint = color,
                )
            }
        }
    }
}

class ComposeThunderDetectionActivityHolder(
    mapState: MapState,
    flushCallouts: () -> Unit
) : Composer<ThunderDetectionActivity, MainScreenModelState>(
    mapState,
    AppColor.RedDarker,
    flushCallouts,
    clusterWidth = 20.dp,
    clusterFontSize = 8.sp
) {
    override fun compose(
        states: MainScreenModelState,
        model: ThunderDetectionActivity,
        xy: XY,
        onSelected: (ThunderDetectionActivity) -> Unit
    ): @Composable (() -> Unit) = {
        Canvas(
            modifier = Modifier.width(8.dp).height(8.dp)
                .clickable { onSelected(model) },
            onDraw = {
                drawCircle(
                    color = model.activityAge(states.selectedDate).color,
                    radius = 4.dp.toPx(),
                )
            }
        )
    }
}

class ComposeAeroguardPositionHolder(
    mapState: MapState,
    flushCallouts: () -> Unit
) : Composer<AeroguardLocation, MainScreenModelState>(
    mapState,
    AppColor.RedDarker,
    flushCallouts,
    clusterWidth = 20.dp,
    clusterFontSize = 8.sp
) {
    override fun compose(
        states: MainScreenModelState,
        model: AeroguardLocation,
        xy: XY,
        onSelected: (AeroguardLocation) -> Unit
    ): @Composable (() -> Unit) = {
        Column(
            Modifier.width(10.dp).height(10.dp)
                .clip(
                    RoundedCornerShape(
                        topStart = 5.dp,
                        topEnd = 5.dp,
                        bottomStart = 5.dp,
                        bottomEnd = 5.dp
                    )
                )
                .background(Color.Black),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                Modifier.width(8.dp).height(8.dp)
                    .clip(
                        RoundedCornerShape(
                            topStart = 4.dp,
                            topEnd = 4.dp,
                            bottomStart = 4.dp,
                            bottomEnd = 4.dp
                        )
                    )
                    .background(AppColor.Blue)
            ) {
                // nothing
            }
        }
    }
}
